/**
 * Business Day Calculator Utility
 * Working Days: Monday - Saturday (excludes only Sundays and holidays)
 */

// Define your organization's holidays here (format: 'YYYY-MM-DD')
export const holidays = [
 // 2026 Holidays (India - adjust based on your region)
  '2026-01-01', // Republic Day
  '2026-01-14', // Republic Day
  '2026-01-26', // Republic Day
  '2026-02-01', // Sunday
  '2026-02-08', // Sunday
  '2026-03-19', // Holi
  '2026-04-03', // Good Friday
  '2026-05-01', // May Day
  '2026-08-15', // Independence Day
  '2026-09-14', // Janmashtami
  '2026-10-20', // Gandhi Jayanti
  '2026-11-01', // Dussehra
  '2026-11-07', // Diwali
  '2026-12-25', // Christmas
  
  // Add 2027 holidays and beyond as needed
  '2027-01-26', // Republic Day
  '2027-12-25', // Christmas
  // ... add more holidays
];

/**
 * Check if a date is a holiday
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is a holiday
 */
export const isHoliday = (date) => {
  const dateStr = date.toISOString().split('T')[0];
  return holidays.includes(dateStr);
};

/**
 * Check if a date is Sunday
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is Sunday
 */
export const isSunday = (date) => {
  return date.getDay() === 0;
};

/**
 * Check if a date is Saturday
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is Saturday
 */
export const isSaturday = (date) => {
  return date.getDay() === 6;
};

/**
 * Check if a date is a working day
 * Working days: Monday - Saturday (excludes only Sundays and holidays)
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is a working day
 */
export const isWorkingDay = (date) => {
  // Only exclude Sundays and holidays
  // Saturdays ARE working days
  if (isSunday(date) || isHoliday(date)) {
    return false;
  }
  
  return true;
};

/**
 * Calculate business days between two dates (inclusive)
 * Counts Monday - Saturday as working days
 * @param {Date|string} startDate - The start date
 * @param {Date|string} endDate - The end date
 * @returns {number} - Number of business days
 */
export const getBusinessDaysBetween = (startDate, endDate) => {
  let count = 0;
  const currentDate = new Date(startDate);
  const end = new Date(endDate);
  
  // Normalize times to compare dates only
  currentDate.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  while (currentDate <= end) {
    if (isWorkingDay(currentDate)) {
      count++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return count;
};

/**
 * Add business days to a date
 * @param {Date|string} startDate - The starting date
 * @param {number} daysToAdd - Number of business days to add
 * @returns {Date} - The resulting date
 */
export const addBusinessDays = (startDate, daysToAdd) => {
  const resultDate = new Date(startDate);
  let addedDays = 0;
  
  if (daysToAdd === 0) {
    return resultDate;
  }
  
  while (addedDays < daysToAdd) {
    resultDate.setDate(resultDate.getDate() + 1);
    if (isWorkingDay(resultDate)) {
      addedDays++;
    }
  }
  
  return resultDate;
};

/**
 * Subtract business days from a date
 * @param {Date|string} startDate - The starting date
 * @param {number} daysToSubtract - Number of business days to subtract
 * @returns {Date} - The resulting date
 */
export const subtractBusinessDays = (startDate, daysToSubtract) => {
  const resultDate = new Date(startDate);
  let subtractedDays = 0;
  
  if (daysToSubtract === 0) {
    return resultDate;
  }
  
  while (subtractedDays < daysToSubtract) {
    resultDate.setDate(resultDate.getDate() - 1);
    if (isWorkingDay(resultDate)) {
      subtractedDays++;
    }
  }
  
  return resultDate;
};

/**
 * Get the next working day after a given date
 * @param {Date|string} date - The starting date
 * @returns {Date} - The next working day
 */
export const getNextWorkingDay = (date) => {
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);
  
  while (!isWorkingDay(nextDay)) {
    nextDay.setDate(nextDay.getDate() + 1);
  }
  
  return nextDay;
};

/**
 * Get the previous working day before a given date
 * @param {Date|string} date - The starting date
 * @returns {Date} - The previous working day
 */
export const getPreviousWorkingDay = (date) => {
  const prevDay = new Date(date);
  prevDay.setDate(prevDay.getDate() - 1);
  
  while (!isWorkingDay(prevDay)) {
    prevDay.setDate(prevDay.getDate() - 1);
  }
  
  return prevDay;
};

/**
 * Check if the current date is a working day, if not return the next working day
 * @param {Date|string} date - The date to check
 * @returns {Date} - The date if it's a working day, otherwise the next working day
 */
export const ensureWorkingDay = (date) => {
  const checkDate = new Date(date);
  
  if (isWorkingDay(checkDate)) {
    return checkDate;
  }
  
  return getNextWorkingDay(checkDate);
};

/**
 * Get all working days between two dates
 * @param {Date|string} startDate - The start date
 * @param {Date|string} endDate - The end date
 * @returns {Date[]} - Array of working days
 */
export const getWorkingDaysList = (startDate, endDate) => {
  const workingDays = [];
  const currentDate = new Date(startDate);
  const end = new Date(endDate);
  
  currentDate.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  while (currentDate <= end) {
    if (isWorkingDay(currentDate)) {
      workingDays.push(new Date(currentDate));
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return workingDays;
};

/**
 * Format business day duration for display
 * @param {number} days - Number of business days
 * @returns {string} - Formatted string (e.g., "5 business days")
 */
export const formatBusinessDays = (days) => {
  if (days === 0) return '0 days';
  if (days === 1) return '1 day';
  return `${days} days`;
};

/**
 * Get working hours in a date range (assuming 8 hours per working day)
 * @param {Date|string} startDate - The start date
 * @param {Date|string} endDate - The end date
 * @param {number} hoursPerDay - Hours per working day (default: 8)
 * @returns {number} - Total working hours
 */
export const getWorkingHours = (startDate, endDate, hoursPerDay = 8) => {
  const businessDays = getBusinessDaysBetween(startDate, endDate);
  return businessDays * hoursPerDay;
};

export default {
  holidays,
  isHoliday,
  isSunday,
  isSaturday,
  isWorkingDay,
  getBusinessDaysBetween,
  addBusinessDays,
  subtractBusinessDays,
  getNextWorkingDay,
  getPreviousWorkingDay,
  ensureWorkingDay,
  getWorkingDaysList,
  formatBusinessDays,
  getWorkingHours
};